/**
 * URL-hash dialog manager — makes global modals deep-linkable from any route.
 *
 * Hash syntax (orthogonal to path/search params, so no per-route wiring):
 *   #settings          → Settings modal, default tab
 *   #settings/branding → Settings modal, Branding tab
 *   #settings/team     → Settings modal, Team tab
 *   #support           → Support / report-bug modal
 *   (no hash)          → nothing open
 *
 * Users can share "point me at this view" URLs and Claude can be pointed at
 * exact UI states. The hash never enters router search validation.
 */

import { useCallback, useEffect, useState } from 'react';

export type UrlDialog = 'settings' | 'support';

export interface UrlDialogState {
  /** Which dialog is open, null when none. */
  dialog: UrlDialog | null;
  /** Sub-state (e.g. the settings tab). Empty string when unset. */
  tab: string;
}

const parseHash = (hash: string): UrlDialogState => {
  const clean = hash.replace(/^#/, '').trim();
  if (!clean) return { dialog: null, tab: '' };
  const [dialog, tab] = clean.split('/');
  if (dialog === 'settings' || dialog === 'support') {
    return { dialog, tab: (tab || '').toLowerCase() };
  }
  return { dialog: null, tab: '' };
};

const serializeState = (state: UrlDialogState): string =>
  state.dialog ? (state.tab ? `#${state.dialog}/${state.tab}` : `#${state.dialog}`) : '';

/**
 * Read/write the dialog state in the URL hash. Opening a dialog pushes a new
 * history entry (back button closes it); closing pops it. Listen for popstate
 * + hashchange so external navigation (shared link, back button) stays in sync.
 */
export function useUrlDialog(): {
  dialog: UrlDialog | null;
  tab: string;
  openDialog: (dialog: UrlDialog, tab?: string) => void;
  closeDialog: () => void;
  setTab: (tab: string) => void;
} {
  const [state, setState] = useState<UrlDialogState>(() => parseHash(window.location.hash));

  useEffect(() => {
    const syncFromLocation = () => setState(parseHash(window.location.hash));
    window.addEventListener('hashchange', syncFromLocation);
    window.addEventListener('popstate', syncFromLocation);
    return () => {
      window.removeEventListener('hashchange', syncFromLocation);
      window.removeEventListener('popstate', syncFromLocation);
    };
  }, []);

  const openDialog = useCallback((dialog: UrlDialog, tab = '') => {
    const next = serializeState({ dialog, tab });
    // pushState so the browser back button closes the dialog instead of
    // leaving the page.
    window.history.pushState(null, '', next || window.location.pathname + window.location.search);
    setState({ dialog, tab });
  }, []);

  const closeDialog = useCallback(() => {
    if (!window.location.hash) return;
    window.history.back();
  }, []);

  const setTab = useCallback((tab: string) => {
    setState((current) => {
      if (current.dialog) {
        window.history.replaceState(null, '', serializeState({ ...current, tab }));
      }
      return current.dialog ? { ...current, tab } : current;
    });
  }, []);

  return {
    dialog: state.dialog,
    tab: state.tab,
    openDialog,
    closeDialog,
    setTab,
  };
}
